// ═══════════════════════════════════════════════════════════════
// Timesheet UI Prototype — Type Definitions
// Pure UI types. No backend coupling. Designed for future invoice mapping.
// ═══════════════════════════════════════════════════════════════

// ── Enums ──

export enum TimesheetDayStatus {
  Draft = "Draft",
  Submitted = "Submitted",
  Approved = "Approved",
  Rejected = "Rejected",
}

export enum VehicleType {
  Ute = "Ute",
  UteAndTrailer = "Ute & Trailer",
  SmallTruck = "Small Truck",
  LargeTruck = "Large Truck",
  Other = "Other",
}

// ── Worker Timesheet (per worker per day) ──

export interface TimesheetDay {
  id: string;
  userId: string;
  userName: string;
  userRole: string; // auto-derived, read-only
  date: string; // YYYY-MM-DD
  status: TimesheetDayStatus;
  workEntries: WorkEntry[];
  assetTravelEntries: AssetTravelEntry[];
  consumablesNote: string;
  notes: string;
  submittedAt: string | null;
  createdByUserId: string | null; // null = self, set = admin on behalf

  // Day-level travel (DEPRECATED — kept for backward compat with admin screen)
  dailyTravelMinutes: number;
  dailyVehicleId: string | null;
  dailyVehicleName: string | null;

  // Per-activity travel (correct model — vehicle is assigned at activity level)
  activityTravelEntries: ActivityTravelEntry[];

  // Approval
  approvedAt: string | null;
  approvedByUserId: string | null;
  rejectedAt: string | null;
  rejectedReason: string | null;

  // derived — recalculated on every mutation
  totalJobHours: number;
  totalStandbyMinutes: number; // renamed from totalIdleMinutes
  totalBreakMinutes: number;
  totalEffectiveHours: number; // total span - total break
  totalActualWorkHours: number; // effective - standby
  entryCount: number;
}

// ── Work Entry (per task per time window) ──

export interface WorkEntry {
  id: string;
  workOrderId: string;
  workOrderNumber: string; // "WO-0042"
  scaffoldTaskId: string;
  scaffoldNumber: string; // "SCF-0012"
  taskName: string; // "Erect main tower"
  clientName: string;
  siteName: string;
  startTime: string; // HH:mm — MANDATORY
  endTime: string; // HH:mm — MANDATORY
  breakMinutes: number; // default 0
  standbyMinutes: number; // waiting/idle time (renamed from idleMinutes)
  // Derived fields — recalculated on every change
  totalSpanMinutes: number; // (end - start) in minutes
  effectiveMinutes: number; // totalSpan - break
  actualWorkMinutes: number; // effective - standby
  calculatedJobMinutes: number; // alias for actualWorkMinutes (backward compat)
  calculatedJobHours: number; // actualWorkMinutes / 60
  notes: string;
  sequence: number;
}

// ── Asset Travel Entry (1 per asset per day — KM source) ──

export type TripType = "Travel" | "Cartage";

export interface AssetTravelEntry {
  id: string;
  assetId: string;
  assetName: string; // "Ute K001"
  assetCode: string; // "VEH-001"
  assetType: VehicleType;
  tripType: TripType; // "Travel" = people, "Cartage" = materials
  startKM: number;
  endKM: number;
  totalKM: number; // derived: endKM - startKM
  startKMPhotoUrl: string | null; // optional odometer photo proof
  endKMPhotoUrl: string | null; // optional odometer photo proof
  createdByUserId: string; // which crew member entered this
  createdByUserName: string;
  distributions: TravelDistribution[];
}

// ── Activity Travel (per-activity travel time — vehicle is assigned at activity level) ──

export interface ActivityTravelEntry {
  id: string;
  activityId: string;
  activityNumber: string;
  vehicleId: string;
  vehicleName: string;
  travelMinutes: number;
}

// ── Travel Distribution (KM split per WO — admin manages %) ──

export interface TravelDistribution {
  workOrderId: string;
  workOrderNumber: string;
  clientName: string;
  jobHoursOnDay: number; // from work entries for auto-calc
  percentage: number; // must sum to 100 across all lines for this asset
  allocatedKM: number; // derived: totalKM * percentage / 100
}

// ── Asset Usage (optional — 1 per asset per WO per day) ──

export interface AssetUsageEntry {
  id: string;
  assetId: string;
  assetName: string;
  assetCode: string;
  workOrderId: string;
  workOrderNumber: string;
  hoursUsed: number;
  notes: string;
}

// ── Planner Activity (for activity-driven task picker) ──

export interface PlannerActivityMock {
  id: string;
  activityNumber: string; // "ACT000067"
  workOrderId: string; // primary WO
  workOrderNumber: string; // "WO-0042"
  jobNumber: string; // "JOB-2026-036"
  jobTitle: string; // "Kalp test"
  clientName: string;
  siteName: string;
  siteAddress: string;
  scheduledStart: string;
  scheduledEnd: string;
  executionLines: ExecutionLineMock[];
  // Additional WOs for this activity (multi-WO support)
  additionalWorkOrders?: { id: string; number: string; clientName: string }[];
}

export interface ExecutionLineMock {
  id: string;
  scaffoldId: string;
  scaffoldNumber: string; // "SCF0001"
  scaffoldName: string; // "Scaffold Around Play Area"
  scaffoldTaskType: string; // "ER", "MO", "DM", "GEN", "IN", "MN"
  scaffoldTaskTypeFull: string; // "Erect", "Modify", "Dismantle", "General"
  taskDescription: string; // "Erect Structure", "ForkLift", "NET around site"
  workOrderTaskId: string;
  scaffoldTaskId: string;
}

// ── Picker Options (mock data shape) ──

export interface WorkOrderOption {
  id: string;
  number: string; // "WO-0042"
  clientName: string;
  siteName: string;
  jobNumber: string; // "JOB-2026-036"
}

export interface ScaffoldTaskOption {
  id: string;
  scaffoldId: string;
  scaffoldNumber: string; // "SCF-0012"
  taskName: string; // "Erect main tower"
  actionIntent: string; // "Erect" | "Modify" | "Dismantle"
  workOrderId: string; // filter key
}

export interface AssetOption {
  id: string;
  name: string; // "Ute K001"
  code: string; // "VEH-001"
  type: VehicleType;
}

export interface WorkerOption {
  id: string;
  name: string;
  role: string; // "Supervisor" | "Advanced Scaffolder" | "Scaffolder"
}

// ── Utility Types ──

export interface TimesheetFilters {
  dateFrom: string;
  dateTo: string;
  workerId: string | null;
  status: TimesheetDayStatus | null;
  search: string;
}

/** For the auto/equal/manual distribution mode */
export type DistributionMode = "proportional" | "equal" | "manual";
